/**
 * The orders behind one of the website tiles on the reports screen.
 *
 * The tile says two orders were declined; this is the two: when each was
 * placed, when and by whom it was declined and why, who ordered it, where it
 * was going, and what was in it. The same for the accepted, the cancelled and
 * the waiting, or all of them at once.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR, formatServerTime } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';

const FILTERS = [
  { key: 'all', label: 'Came in' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'declined', label: 'Declined' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'waiting', label: 'Waiting' },
];

const STATUS = {
  accepted: { label: 'Accepted', tone: 'bg-good/15 text-good' },
  declined: { label: 'Declined', tone: 'bg-bad/15 text-bad' },
  cancelled: { label: 'Cancelled by customer', tone: 'bg-surface-3 text-ink-2' },
  waiting: { label: 'Waiting', tone: 'bg-warn/15 text-warn' },
};

/** "after 3 min", "after 1 h 5 min": how long the decision took. */
function after(fromIso, toIso) {
  const minutes = Math.max(0, Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 60_000));
  if (minutes < 1) return 'within a minute';
  if (minutes < 60) return `after ${minutes} min`;
  return `after ${Math.floor(minutes / 60)} h${minutes % 60 ? ` ${minutes % 60} min` : ''}`;
}

/** What became of the order, and when, in a line. */
function decision(order) {
  if (order.status === 'waiting' || !order.decidedAt) return 'Not answered yet';
  const when = `${formatServerTime(order.decidedAt)} (${after(order.placedAt, order.decidedAt)})`;
  if (order.status === 'accepted') return `Accepted ${when}${order.decidedBy ? ` by ${order.decidedBy}` : ''}`;
  if (order.status === 'declined') return `Declined ${when}${order.decidedBy ? ` by ${order.decidedBy}` : ''}`;
  return `Cancelled by the customer ${when}`;
}

export function WebsiteOrdersSheet({ status: first = 'all', from, to, onClose }) {
  const [status, setStatus] = useState(first);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['reports', 'website-orders', from, to],
    queryFn: () => api.get(withQuery('/reports/website-orders', { from, to })),
  });
  const all = data?.orders ?? [];
  const shown = status === 'all' ? all : all.filter((o) => o.status === status);
  const count = (key) => (key === 'all' ? all.length : all.filter((o) => o.status === key).length);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Website orders"
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4"
      onClick={(e) => {
        // Outside the sheet closes it; inside does not.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl rounded-2xl bg-surface p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-ink">Website orders</h2>
            <p className="text-sm text-ink-2">{from === to ? from : `${from} to ${to}`}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg px-2 py-1 text-xl leading-none text-ink-3 hover:bg-surface-3">
            ×
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2" role="tablist" aria-label="Which orders">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={status === f.key}
              onClick={() => setStatus(f.key)}
              className={`rounded-full px-3 py-1 text-sm ${status === f.key ? 'bg-brand-400 font-medium text-black' : 'bg-surface-3 text-ink-2 hover:text-ink'}`}
            >
              {f.label}
              {data ? <span className="ml-1 tabular-nums opacity-70">{count(f.key)}</span> : null}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner label="Loading" />
          </div>
        ) : isError ? (
          <p role="alert" className="py-10 text-center text-sm text-bad">
            {error?.message ?? 'Could not load these orders.'}
          </p>
        ) : shown.length === 0 ? (
          <p className="py-10 text-center text-sm text-ink-3">No orders here in these dates.</p>
        ) : (
          <ul className="mt-3">
            {shown.map((order) => (
              <li key={order.id} className="border-t border-rule py-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="min-w-0 text-sm">
                    <span className="font-semibold text-ink">{order.reference}</span>
                    {order.orderNumber && <span className="text-ink-3"> · bill {order.orderNumber}</span>}
                    <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS[order.status]?.tone ?? ''}`}>{STATUS[order.status]?.label ?? order.status}</span>
                  </p>
                  <p className="shrink-0 font-semibold tabular-nums text-ink">{formatPKR(order.total)}</p>
                </div>

                <dl className="mt-2 grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-ink-3">Placed</dt>
                  <dd className="text-ink-2">{formatServerTime(order.placedAt)}</dd>
                  <dt className="text-ink-3">Outcome</dt>
                  <dd className="text-ink-2">{decision(order)}</dd>
                  {order.declineReason && (
                    <>
                      <dt className="text-ink-3">Reason</dt>
                      <dd className="text-bad">{order.declineReason}</dd>
                    </>
                  )}
                  <dt className="text-ink-3">Customer</dt>
                  <dd className="text-ink-2">{[order.customer.name, order.customer.phone].filter(Boolean).join(' · ')}</dd>
                  <dt className="text-ink-3">{order.fulfilment === 'pickup' ? 'Pickup' : 'Delivery'}</dt>
                  <dd className="text-ink-2">
                    {order.fulfilment === 'pickup' ? 'Collected at the counter' : [order.address, order.distanceKm != null ? `${order.distanceKm} km` : null].filter(Boolean).join(' · ') || '—'}
                  </dd>
                  {order.note && (
                    <>
                      <dt className="text-ink-3">Note</dt>
                      <dd className="text-ink-2">{order.note}</dd>
                    </>
                  )}
                </dl>

                <table className="mt-3 w-full text-sm">
                  <tbody>
                    {order.items.map((item, index) => (
                      <tr key={index} className="align-top text-ink-2">
                        <td className="w-10 py-0.5 pr-2 tabular-nums">{item.qty} ×</td>
                        <td className="py-0.5 pr-2">
                          {item.name}
                          {item.choices.length > 0 && <span className="block text-xs text-ink-3">{item.choices.join(', ')}</span>}
                        </td>
                        <td className="py-0.5 text-right tabular-nums">{formatPKR(item.total)}</td>
                      </tr>
                    ))}
                    {order.deliveryFee > 0 && (
                      <tr className="text-ink-3">
                        <td />
                        <td className="py-0.5 pr-2">Delivery</td>
                        <td className="py-0.5 text-right tabular-nums">{formatPKR(order.deliveryFee)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
